import { useCallback, useRef } from 'react';

/**
 * A hook that wraps a mutation function to automatically cancel the previous
 * request when a new one is made.
 *
 * @param mutationFn - The mutation function that accepts data and an optional AbortSignal
 * @returns A wrapped mutation function that cancels previous pending requests
 */
export const useCancellableMutationFn = <TData, TResponse>(
    mutationFn: (data: TData, signal?: AbortSignal) => Promise<TResponse>,
) => {
    const abortControllerRef = useRef<AbortController | null>(null);

    return useCallback(
        (data: TData) => {
            // Cancel the previous request if it's still pending
            abortControllerRef.current?.abort();

            // Create a new AbortController for this request
            abortControllerRef.current = new AbortController();

            return mutationFn(data, abortControllerRef.current.signal);
        },
        [mutationFn],
    );
};
